import Link from "next/link";
import { TagS } from "@/components/molecules/TagS";
import { useBudouX } from "@/hooks/useBudouX";

// AI活用事例セクション（/project 既存実績の下に表示）
// ターゲット3像（早く形にしたい／内製化に苦戦／研修）別に自社の実践事例を並べる
// 各カードは専用の詳細ページ（/project/ai/<slug>）へ遷移（詳細ページは別途作成予定）

const GROUPS = [
  {
    heading: "高速プロトタイプ開発で、早く形にしたい方へ",
    sub: "早い段階から“動くもの”を見て、確かめながら進められます。",
    cta: "高速プロトタイプ開発を相談する",
    cases: [
      {
        href: "/project/ai/rapid-prototype",
        pastel: "bg-project-no1",
        img: "/ai-case/ai-case-g1-1.webp",
        label: "試作・検証の高速化",
        title: "「動かして試す」を即日に。早い段階から実物で確認",
        tags: ["プロトタイプ", "PoC"],
      },
      {
        href: "/project/ai/fast-release",
        pastel: "bg-project-no2",
        img: "/ai-case/ai-case-g1-2.webp",
        label: "短期リリース",
        title: "3週間で新サービスをリリースした若手エンジニアの話",
        tags: ["短期リリース", "高速開発"],
      },
      {
        href: "/project/ai/large-scale-dev",
        pastel: "bg-project-no3",
        img: "/ai-case/ai-case-g1-3.webp",
        label: "大規模開発の短縮",
        title: "業務システムをまるごと、従来の約1/3の期間で構築",
        tags: ["大規模開発", "AI"],
      },
    ],
  },
  {
    heading: "社内のAI内製化・定着に苦戦している方へ",
    sub: "「一部の人が試す」で止まらない。組織に根付かせた実践。",
    cta: "AI内製化・社内展開を相談する",
    cases: [
      {
        href: "/project/ai/org-adoption",
        pastel: "bg-project-no1",
        img: "/ai-case/ai-case-g2-1.webp",
        label: "組織への定着",
        title: "「一部で試す」から「全案件でAI駆動を標準化」へ",
        tags: ["内製化", "標準化"],
      },
      {
        href: "/project/ai/governance",
        pastel: "bg-project-no2",
        img: "/ai-case/ai-case-g2-2.webp",
        label: "ルール整備・安心",
        title: "AIを“使っていい線”を引く。倫理とガバナンスの実務",
        tags: ["ガバナンス", "セキュリティ"],
      },
      {
        href: "/project/ai/roi-measurement",
        pastel: "bg-project-no3",
        img: "/ai-case/ai-case-g2-3.webp",
        label: "効果測定・経営説明",
        title: "「なんとなく便利」で終わらせない。効果をROIで数字化",
        tags: ["効果測定", "ROI"],
      },
    ],
  },
  {
    heading: "AI研修・人材育成をお探しの方へ",
    sub: "座学で終わらせない。“使える”に変える育成の実績。",
    cta: "AI研修・人材育成を相談する",
    cases: [
      {
        href: "/project/ai/training",
        pastel: "bg-project-no1",
        img: "/ai-case/ai-case-g3-1.webp",
        label: "全社研修",
        title: "「使える人／使えない人」の格差を埋める週次AI研修",
        tags: ["研修", "AIリテラシー"],
      },
      {
        href: "/project/ai/onboarding",
        pastel: "bg-project-no2",
        img: "/ai-case/ai-case-g3-2.webp",
        label: "早期戦力化",
        title: "過去の経験を活かし、AIで早期に立ち上げる",
        tags: ["人材育成", "早期戦力化"],
      },
      {
        href: "/project/ai/ai-assessment",
        pastel: "bg-project-no3",
        img: "/ai-case/ai-case-g3-3.webp",
        label: "AI活用の見極め",
        title: "「AIで何かできそう」を、まず小さく試して見極める",
        tags: ["PoC", "業務評価"],
      },
    ],
  },
];

export const AiCaseStudies = () => {
  const { parse } = useBudouX();
  return (
    <section className="mt-12 md:mt-20">
      <div className="mb-8 bg-bgc-dark p-6 md:mb-10 md:p-10">
        <h6 className="mb-3 text-sm font-bold text-pri">Case Studies</h6>
        <h2 className="mb-4 font-bold leading-relaxed text-pri fluid-text-[20px,30px]">
          {parse(
            "「早く形にしたい」「社内で広めたい」「学びたい」。その一歩を、私たち自身の実践で。"
          )}
        </h2>
        <p className="max-w-3xl text-sm leading-loose text-txt-base">
          {parse(
            "お客様に提案する前に、まず自分たちで試し、効果を測っています。高速プロトタイプで早く形にしたい方、社内の内製化・定着に苦戦している方、研修をお探しの方へ、リアルな実践事例でお応えします。"
          )}
        </p>
      </div>

      <div className="px-6 sm:px-8 md:px-12 xl:px-0">
        {GROUPS.map((group) => (
          <div key={group.heading} className="mb-10 md:mb-14">
            <div className="mb-5">
              <p className="mb-1 text-xs font-bold text-pri">For</p>
              <h3 className="font-bold text-pri fluid-text-[16px,20px]">
                {parse(group.heading)}
              </h3>
              <p className="mt-1 text-sm text-txt-light">{parse(group.sub)}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
              {group.cases.map((c) => (
                <Link key={c.title} href={c.href}>
                  <a className="group flex cursor-pointer flex-col border border-solid border-bdc-light transition-colors hover:border-hov/30">
                    <div
                      className={`overflow-hidden ${c.pastel}`}
                      style={{ aspectRatio: "1.9 / 1" }}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={c.img}
                        alt={c.label}
                        className="h-full w-full object-cover transition-opacity group-hover:opacity-80"
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-2 py-3 px-3 md:gap-3 md:py-4 md:px-5">
                      <p className="text-xs font-bold leading-none text-icn">
                        {c.label}
                      </p>
                      <h4 className="font-bold leading-relaxed text-txt-base transition-colors group-hover:text-hov fluid-text-[13px,15px]">
                        {parse(c.title)}
                      </h4>
                      <div className="mt-auto flex flex-wrap justify-end gap-2 pt-1 md:gap-3">
                        {c.tags.map((t) => (
                          <TagS key={t}>{t}</TagS>
                        ))}
                      </div>
                    </div>
                  </a>
                </Link>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <Link href="/contact/form/">
                <a className="inline-flex items-center gap-2 text-sm font-bold text-pri transition-colors hover:text-hov">
                  {group.cta} <span>→</span>
                </a>
              </Link>
            </div>
          </div>
        ))}

        <p className="mt-6 text-xs text-txt-light">
          ※ 各事例は、機密情報保持契約を遵守した上で、特定されないよう配慮して掲載しています。
        </p>
      </div>
    </section>
  );
};
